import React, { useState, useCallback, useEffect } from "react";
import Navbar from "../../../components/Navbar";
import ProjectDetailSection from "./CAFSections/ProjectDetailSection";
import LocationSection from "./CAFSections/LocationSection";
import ActivityCostSection from "./CAFSections/ProjectCostSection";
import OtherSection from "./CAFSections/OtherSection";
import "./CAF.css";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../../services/api";

const CAF = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const databaseProjectId = queryParams.get("projectId");
  const [userRole, setUserRole] = useState("");
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    projectDetails: {
      projectName: "",
      projectProposal: "",
      projectId: "",
      description: "",
      legacyStatus: "",
      companyName: "",
      address: "",
      city: "",
      state: "",
      district: "",
      pincode: "",
      landmarks: "",
      email: "",
      landlineNumber: "",
      phoneNumber: "",
    },
    projectLocation: {
      kmlFile: "",
      internationalBorder: "",
      projectShape: "",
      nonForestLand: "",
      forestLand: "",
      totalLand: "",
    },
    projectCost: {
      totalCost: "",
      constructionPermanentEmployees: "",
      constructionEmploymentPeriod: "",
      constructionManDays: "",
      constructionTemporaryEmployment: "",
      constructionTotal: "",
      operationPermanentEmployees: "",
      operationEmploymentPeriod: "",
      operationManDays: "",
      operationTemporaryEmployment: "",
      operationTotal: "",
    },
    OtherSection: {
      isRRInvolved: "",
      numVillages: "",
      numDisplacedFamilies: "",
      numAffectedFamilies: "",
      rehabilitationStatus: "",
      siteShifting: "",
      siteShiftingDetails: "",
      alternativeSites: "",
      alternativeSiteDetails: "",
      siteJustification: "",
      governmentOrder: "",
      governmentOrderDetails: "",
      pendingLitigation: "",
      courtName: "",
      specificCourt: "",
      caseCategory: "",
      caseStatus: "",
      courtDirections: "",
      violationInvolved: "",
      violationJustification: "",
      alternativeKMLFile: null,
      governmentOrderFile: null,
      litigationDocuments: null,
      courtDirectionsFile: null,
      violationDocuments: null,
    },
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (databaseProjectId) {
      loadExistingCAF();
    }

    const fetchUserRole = async () => {
      try {
        // Get user data from local storage or from API
        const userData = JSON.parse(localStorage.getItem("user"));
        if (userData && userData.role) {
          setUserRole(userData.role);
        } else {
          // If not in localStorage, try to get from API
          const response = await api.get("/api/user/profile");
          if (response.data && response.data.role) {
            setUserRole(response.data.role);
          }
        }
      } catch (error) {
        console.error("Error fetching user role:", error);
        // Default role if we can't determine it
        setUserRole("user");
      }
    };

    fetchUserRole();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [databaseProjectId]);

  const role = userRole;

  const loadExistingCAF = async () => {
    try {
      const response = await api.get(`/api/caf/${databaseProjectId}`);
      if (response.data && response.data.success) {
        const cafData = response.data.data;
        if (cafData && cafData.content) {
          setFormData(cafData.content);
        }
      }
    } catch (error) {
      if (error.response && error.response.status !== 404) {
        console.error("Error loading CAF data:", error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();

      if (!databaseProjectId) {
        alert("Missing project association. Please start from project page.");
        navigate("/admin/manage-projects");
        return;
      }
      if (formData.OtherSection.alternativeKMLFile) {
        formDataToSend.append(
          "alternativeKMLFile",
          formData.OtherSection.alternativeKMLFile
        );
      }

      // Append the KML file if it exists
      if (formData.projectLocation.kmlFile) {
        formDataToSend.append("kmlFile", formData.projectLocation.kmlFile);
      }

      const otherSectionFiles = [
        "alternativeKMLFile",
        "governmentOrderFile",
        "litigationDocuments",
        "courtDirectionsFile",
        "violationDocuments",
      ];

      otherSectionFiles.forEach((fieldName) => {
        if (formData.OtherSection[fieldName]) {
          formDataToSend.append(fieldName, formData.OtherSection[fieldName]);
        }
      });

      const cleanFormData = {
        ...formData,
        projectLocation: {
          ...formData.projectLocation,
          kmlFile: undefined,
        },
        OtherSection: {
          ...formData.OtherSection,
          alternativeKMLFile: undefined,
          governmentOrderFile: undefined,
          litigationDocuments: undefined,
          courtDirectionsFile: undefined,
          violationDocuments: undefined,
        },
      };

      formDataToSend.append(
        "title",
        formData.projectDetails.projectName || "Common Application Form"
      );

      formDataToSend.append("projectId", databaseProjectId);

      formDataToSend.append("projectData", JSON.stringify(cleanFormData));

      // Check if CAF already exists for this project
      let cafExists = false;
      let cafId = null;

      try {
        const checkResponse = await api.get(
          `/api/projects/${databaseProjectId}/created-documents`
        );
        if (checkResponse.data && checkResponse.data.documents) {
          const existingCaf = checkResponse.data.documents.find(
            (doc) => doc.type === "caf"
          );
          if (existingCaf) {
            cafExists = true;
            cafId = existingCaf.id;
          }
        }
      } catch (error) {
        console.error("Error checking existing CAF:", error);
      }

      let response;

      if (cafExists && cafId) {
        // Update existing CAF
        response = await api.put(`/api/caf/${cafId}`, formDataToSend, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        // Create new CAF
        response = await api.post("/api/caf", formDataToSend, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      }

      if (response.data) {
        alert(
          cafExists ? "CAF updated successfully!" : "CAF created successfully!"
        );
        navigate("/admin/manage-projects");
      }
    } catch (error) {
      console.error("Error with CAF:", error);
      alert(error.response?.data?.message || "Failed to process CAF");
    }
  };

  const handleFormDataChange = useCallback((section, data) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        ...data,
      },
    }));
  }, []);

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <ProjectDetailSection
            formData={formData}
            setFormData={handleFormDataChange}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />
        );
      case 2:
        return (
          <LocationSection
            formData={formData}
            setFormData={handleFormDataChange}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />
        );
      case 3:
        return (
          <ActivityCostSection
            formData={formData}
            setFormData={handleFormDataChange}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />
        );
      case 4:
        return (
          <OtherSection
            formData={formData}
            setFormData={handleFormDataChange}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="caf-container">
      <Navbar role={role} />
      <div className="step-indicator">
        {[1, 2, 3, 4].map((step) => (
          <div
            key={step}
            className={`step ${currentStep === step ? "active" : ""} 
                     ${currentStep > step ? "completed" : ""}`}
            onClick={() => setCurrentStep(step)}
          >
            <div className="step-number">{step}</div>
            <div className="step-label">
              {step === 1 && "Project Details"}
              {step === 2 && "Location of Project"}
              {step === 3 && "Project Activity Cost"}
              {step === 4 && "Others"}
            </div>
          </div>
        ))}
      </div>
      <h1 className="caf-heading">Common Application Form</h1>
      <form className="caf-form" onSubmit={handleSubmit}>
        {renderStep()}
        <div className="form-navigation">
          {currentStep > 1 && (
            <button
              type="button"
              onClick={() => setCurrentStep((prev) => prev - 1)}
              className="prev-btn"
            >
              Previous
            </button>
          )}
          {currentStep < 4 && (
            <button
              type="button"
              onClick={() => setCurrentStep((prev) => prev + 1)}
              className="next-btn"
            >
              Next
            </button>
          )}
          {currentStep === 4 && (
            <button type="submit" className="submit-btn">
              {databaseProjectId ? "Update" : "Submit"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default CAF;
